// iter-294 — Dashboard-level Financial Movement card (aggregate across
// every store the merchant owns) + a one-time Flash banner that fires
// when the admin changes commission rates between visits.
//
// Rendering is deliberately conservative — if the merchant has NO
// selling stores (phone_numbers / car_plates only) we render nothing.

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet, Clock, CheckCircle2, ArrowDownUp, AlertCircle,
  X as XIcon, Loader2,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtAED = (v, lang) => {
  const n = Number(v || 0);
  return `${n.toLocaleString(lang === 'ar' ? 'ar-AE' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${lang === 'ar' ? 'د.إ' : 'AED'}`;
};

const CARD_TONES = {
  emerald: { bg: 'bg-emerald-50 border-emerald-100',   bgDark: 'bg-emerald-500/10 border-emerald-400/20',   fg: 'text-emerald-600' },
  blue:    { bg: 'bg-blue-50 border-blue-100',         bgDark: 'bg-blue-500/10 border-blue-400/20',         fg: 'text-blue-600'    },
  amber:   { bg: 'bg-amber-50 border-amber-100',       bgDark: 'bg-amber-500/10 border-amber-400/20',       fg: 'text-amber-600'   },
  slate:   { bg: 'bg-slate-50 border-slate-100',       bgDark: 'bg-slate-500/10 border-slate-400/20',       fg: 'text-slate-700'   },
};

function Card({ Icon, label, value, tone = 'slate', isDarkMode, testId }) {
  const t = CARD_TONES[tone] || CARD_TONES.slate;
  return (
    <div className={`rounded-2xl border p-4 ${isDarkMode ? t.bgDark : t.bg}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <p className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{label}</p>
        <Icon className={`h-4 w-4 ${t.fg}`} />
      </div>
      <p className={`mt-2 text-xl font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// COMMISSION-CHANGE FLASH BANNER
// Fires once when the admin changes commission_percent or admin_fee_amount
// between the merchant's visits. We store the last-seen numbers in
// localStorage; if the backend returns different ones we flash the banner
// then persist the new values so the banner only shows ONCE per change.
// ─────────────────────────────────────────────────────────────────────
const COMMISSION_SNAPSHOT_KEY = 'cgm_commission_snapshot_v1';

export function CommissionChangeBanner({ language, isDarkMode }) {
  const [state, setState] = useState('idle'); // idle | changed | dismissed
  const [current, setCurrent] = useState(null);
  const [previous, setPrevious] = useState(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const token = localStorage.getItem('business_token');
        if (!token) return;
        const { data } = await axios.get(
          `${BACKEND_URL}/api/merchant/commission-settings`,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 }
        );
        if (dead) return;
        const now = {
          commission_enabled: !!data.commission_enabled,
          commission_percent: Number(data.commission_percent || 0),
          admin_fee_enabled: !!data.admin_fee_enabled,
          admin_fee_amount: Number(data.admin_fee_amount || 0),
        };
        // Only pay attention to hub-backed values — stale/safe-default
        // settings must NOT fire a banner (they'd flash on every outage).
        if ((data.source || 'unknown') !== 'hub') {
          return;
        }
        let prev = null;
        try { prev = JSON.parse(localStorage.getItem(COMMISSION_SNAPSHOT_KEY) || 'null'); } catch { /* ignore */ }
        setCurrent(now);
        setPrevious(prev);
        const changed = prev && JSON.stringify(prev) !== JSON.stringify(now);
        if (changed) {
          setState('changed');
        } else {
          // Persist the first-seen snapshot so future changes can be
          // detected.
          localStorage.setItem(COMMISSION_SNAPSHOT_KEY, JSON.stringify(now));
        }
      } catch { /* silent — banner is a nice-to-have */ }
    })();
    return () => { dead = true; };
  }, []);

  const dismiss = () => {
    if (current) localStorage.setItem(COMMISSION_SNAPSHOT_KEY, JSON.stringify(current));
    setState('dismissed');
  };

  if (state !== 'changed' || !current) return null;

  const fmt = (s) => (!s ? '—' : `${s.commission_enabled ? `${s.commission_percent}%` : (language === 'ar' ? 'بدون عمولة' : 'no commission')}` +
    ' • ' + `${s.admin_fee_enabled ? `${s.admin_fee_amount} ${language === 'ar' ? 'د.إ' : 'AED'} ${language === 'ar' ? 'رسوم' : 'fee'}` : (language === 'ar' ? 'بدون رسوم' : 'no fee')}`);

  return (
    <div
      className={`mb-5 rounded-2xl border overflow-hidden shadow-sm ${isDarkMode ? 'bg-gradient-to-r from-amber-500/15 to-blue-500/15 border-amber-400/30' : 'bg-gradient-to-r from-amber-50 to-blue-50 border-amber-200'}`}
      data-testid="commission-change-banner"
    >
      <div className="p-4 flex items-start gap-3">
        <div className={`shrink-0 p-2 rounded-xl ${isDarkMode ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
          <AlertCircle className="h-5 w-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {language === 'ar' ? 'تنبيه: تحديث نسبة العمولة والرسوم' : 'Notice: commission & fee updated'}
          </p>
          <p className={`mt-1 text-xs leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {language === 'ar'
              ? 'منصة الأدمِن حدّثت الإعدادات. الطلبات الجديدة ستُحسب بالنسبة الجديدة:'
              : 'The admin platform updated the settings. New orders will be computed with the new rates:'}
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-black/30' : 'bg-white/70'}`}>
              <span className={`block ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {language === 'ar' ? 'السابق' : 'Previous'}
              </span>
              <span className={`font-mono font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{fmt(previous)}</span>
            </div>
            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100/70'}`}>
              <span className={`block ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>
                {language === 'ar' ? 'الحالي' : 'Current'}
              </span>
              <span className="font-mono font-bold text-emerald-700">{fmt(current)}</span>
            </div>
          </div>
          <p className={`mt-2 text-[11px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {language === 'ar'
              ? 'ملاحظة: الطلبات السابقة محفوظة بنسبها الأصلية (snapshot).'
              : 'Note: existing orders keep their original rates (snapshotted at purchase).'}
          </p>
        </div>
        <button
          onClick={dismiss}
          className={`shrink-0 p-1.5 rounded-full ${isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-black/5 text-gray-500'}`}
          data-testid="commission-banner-dismiss"
          aria-label="dismiss"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DASHBOARD FINANCIAL SUMMARY SECTION
// ─────────────────────────────────────────────────────────────────────
export function DashboardFinancialSummary({ language, isDarkMode }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('business_token');
    if (!token) { setLoading(false); return; }
    axios
      .get(`${BACKEND_URL}/api/merchant/dashboard/financial-summary`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || 'Network error'))
      .finally(() => setLoading(false));
  }, []);

  const overall = data?.overall;
  const perStore = data?.per_store || [];
  const hasSellingStores = (overall?.stores_count || 0) > 0;

  if (loading) {
    return (
      <div className="mb-8 flex items-center justify-center py-6">
        <Loader2 className={`h-6 w-6 animate-spin ${isDarkMode ? 'text-blue-400' : 'text-[#38BDF8]'}`} />
      </div>
    );
  }
  // Deliberately render NOTHING for merchants without a selling store —
  // Phone numbers / car plates don't have a financial-movement model.
  if (!hasSellingStores) return null;
  if (error) return null; // silent — dashboard still works

  // iter-298 — show the section whenever there's ANY customer payment,
  // not just delivered-only lines (otherwise merchants with lots of
  // pending orders see the empty state and panic).
  const hasAnyRevenue = (overall?.total_paid_by_customers || 0) > 0 || (overall?.lines_count || 0) > 0;

  return (
    <section className="mb-8" data-testid="dashboard-financial-summary">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className={`text-lg sm:text-xl font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
          <ArrowDownUp className="h-5 w-5 text-[#1E9BD9]" />
          {language === 'ar' ? 'الحركة المالية الموحّدة' : 'Combined Financial Movement'}
        </h2>
        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {language === 'ar'
            ? `من ${overall.stores_count} ${overall.stores_count === 1 ? 'متجر' : 'متاجر'} • ${overall.orders_count} طلب مُحتسب`
            : `From ${overall.stores_count} store${overall.stores_count === 1 ? '' : 's'} • ${overall.orders_count} orders counted`}
        </span>
      </div>

      {!hasAnyRevenue ? (
        <div className={`rounded-2xl border p-6 text-center ${isDarkMode ? 'bg-[#131B2C]/50 border-white/10' : 'bg-white/80 border-gray-100'}`} data-testid="fin-dashboard-empty">
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {language === 'ar'
              ? 'لا توجد مبيعات مُحتَسبة بعد. ستظهر المبالغ هنا بمجرد أن تُؤكَّد الطلبات.'
              : 'No completed sales yet. Totals will appear once orders are confirmed.'}
          </p>
        </div>
      ) : (
        <>
          {/* iter-298 — Three cards: paid-by-customers, pending wallet, credited wallet */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <Card
              Icon={Wallet}
              tone="slate"
              label={language === 'ar' ? 'إجمالي ما دفعه العملاء' : 'Total paid by customers'}
              value={fmtAED(overall.total_paid_by_customers, language)}
              isDarkMode={isDarkMode}
              testId="dash-fin-paid"
            />
            <Card
              Icon={Clock}
              tone="amber"
              label={language === 'ar' ? 'المبالغ المعلّقة' : 'Pending (reserved)'}
              value={fmtAED(overall.pending_wallet, language)}
              isDarkMode={isDarkMode}
              testId="dash-fin-pending"
            />
            <Card
              Icon={CheckCircle2}
              tone="emerald"
              label={language === 'ar' ? 'المبالغ في المحفظة' : 'Credited to wallet'}
              value={fmtAED(overall.wallet_credited, language)}
              isDarkMode={isDarkMode}
              testId="dash-fin-credited"
            />
          </div>

          {/* Per-store breakdown — only when the merchant has >1 selling store */}
          {perStore.length > 1 && (
            <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? 'border-white/10 bg-[#131B2C]/60' : 'border-gray-100 bg-white/80'}`}>
              <div className={`px-4 py-2.5 text-xs font-semibold border-b ${isDarkMode ? 'bg-white/5 border-white/5 text-gray-300' : 'bg-gray-50 border-gray-100 text-gray-600'}`}>
                {language === 'ar' ? 'التفصيل حسب المتجر' : 'Breakdown by store'}
              </div>
              <ul className="divide-y divide-gray-100 dark:divide-white/5">
                {perStore.map((s) => (
                  <li
                    key={s.store_id}
                    className={`flex items-center justify-between px-4 py-3 ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                    data-testid={`dash-fin-store-${s.store_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/stores/${s.store_id}`}
                        className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'} hover:underline`}
                      >
                        {s.store_name}
                      </Link>
                      <div className={`mt-0.5 text-[11px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {language === 'ar'
                          ? `${s.orders_count} طلب • ${s.lines_count} سطر`
                          : `${s.orders_count} orders • ${s.lines_count} lines`}
                      </div>
                    </div>
                    <div className="text-end flex-shrink-0 ms-3">
                      <p className={`text-sm font-extrabold text-emerald-600`}>{fmtAED(s.wallet_credited, language)}</p>
                      <p className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {language === 'ar' ? 'في محفظتك' : 'in your wallet'}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default DashboardFinancialSummary;
